/**
 * HostingPromoPopup Component
 *
 * Promotional slide-in popup showing hosting prices from partner (Datavex)
 * Slides in from the right, can be dismissed, remembers user preference
 * Includes email input for quote requests
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import type { DatavexPlatformStats } from '../../services/datavexApi';

interface NodesSummary {
  total: number;
  cumulus: number;
  nimbus: number;
  stratus: number;
}

interface HostingPromoPopupProps {
  datavexStats: DatavexPlatformStats | null | undefined;
  wallets?: string[];
  nodesSummary?: NodesSummary;
}

const POPUP_DISMISSED_KEY = 'fluxnode_hosting_promo_dismissed';
const POPUP_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export function HostingPromoPopup({ datavexStats, wallets = [], nodesSummary }: HostingPromoPopupProps) {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Check if popup was dismissed recently
    const dismissedAt = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < POPUP_DISMISS_DURATION) {
        return; // Still within dismiss period
      }
    }

    // Show popup after a delay if we have pricing data
    if (datavexStats?.hosting_price_eur_days) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [datavexStats]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
    }, 300); // Match animation duration
  };

  const handleSubmit = async () => {
    if (!email || isSubmitting) return;

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/hosting-quote/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          wallets,
          nodesSummary: nodesSummary || { total: 0, cumulus: 0, nimbus: 0, stratus: 0 },
          language: i18n.language,
          // Send prices from API for accurate quote
          prices: {
            cumulus: datavexStats?.hosting_price_eur_days?.cumulus?.['30'] || 0,
            nimbus: datavexStats?.hosting_price_eur_days?.nimbus?.['30'] || 0,
            stratus: datavexStats?.hosting_price_eur_days?.stratus?.['30'] || 0,
          },
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Auto-close after success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending quote request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible || !datavexStats?.hosting_price_eur_days) {
    return null;
  }

  const prices = datavexStats.hosting_price_eur_days;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '320px',
        background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.98), rgba(20, 20, 30, 0.98))',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        zIndex: 10001, // Above support chat (9999)
        overflow: 'hidden',
        animation: isClosing
          ? 'slideOutRight 0.3s ease-in forwards'
          : 'slideInRight 0.4s ease-out',
      }}
    >
      {/* Gradient accent bar */}
      <div
        style={{
          height: '4px',
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)',
        }}
      />

      {/* Header */}
      <div style={{ padding: '16px 16px 12px', position: 'relative' }}>
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '8px',
            padding: '6px',
            cursor: 'pointer',
            color: 'var(--muted)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'var(--muted)';
          }}
          aria-label={t('common.close')}
        >
          <Icons.X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.Server size={20} color="white" />
          </div>
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              {t('fluxNode.promo.title', 'Hébergement FluxNode')}
            </h4>
            <p style={{ fontSize: '11px', color: 'var(--muted)', margin: 0 }}>
              {t('fluxNode.promo.subtitle', 'Partenaire officiel')}
            </p>
          </div>
        </div>

        {/* Fun question */}
        <p style={{
          fontSize: '12px',
          color: 'var(--text)',
          margin: '8px 0 0',
          fontStyle: 'italic',
          opacity: 0.9,
        }}>
          {t('fluxNode.promo.funQuestion', '🤔 What about YOUR hosting prices?')}
        </p>
      </div>

      {/* Pricing Grid */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Cumulus */}
          <div
            style={{
              flex: 1,
              background: 'rgba(59, 130, 246, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(59, 130, 246, 0.3)',
            }}
          >
            <div style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 600, marginBottom: '4px' }}>
              CUMULUS
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>
              {prices.cumulus?.['30'] || '—'}€
            </div>
            <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
              /{t('fluxNode.promo.perMonth', 'mois')}
            </div>
          </div>

          {/* Nimbus */}
          <div
            style={{
              flex: 1,
              background: 'rgba(139, 92, 246, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(139, 92, 246, 0.3)',
            }}
          >
            <div style={{ fontSize: '10px', color: '#8b5cf6', fontWeight: 600, marginBottom: '4px' }}>
              NIMBUS
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>
              17.99€
            </div>
            <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
              /{t('fluxNode.promo.perMonth', 'mois')}
            </div>
          </div>

          {/* Stratus */}
          <div
            style={{
              flex: 1,
              background: 'rgba(236, 72, 153, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(236, 72, 153, 0.3)',
            }}
          >
            <div style={{ fontSize: '10px', color: '#ec4899', fontWeight: 600, marginBottom: '4px' }}>
              STRATUS
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>
              34.99€
            </div>
            <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
              /{t('fluxNode.promo.perMonth', 'mois')}
            </div>
          </div>
        </div>

        {/* Features */}
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('fluxNode.promo.dedicatedServer', 'Serveur dédié')}
          </span>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('fluxNode.promo.uniqueIP', 'IP unique')}
          </span>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('fluxNode.promo.alerts', 'Alertes Discord')}
          </span>
        </div>

        {/* Email Input & Submit */}
        {submitStatus === 'success' ? (
          <div style={{
            marginTop: '14px',
            padding: '14px',
            background: 'rgba(16, 185, 129, 0.15)',
            borderRadius: '10px',
            textAlign: 'center',
            color: '#10b981',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}>
            <Icons.Check size={18} />
            {t('fluxNode.promo.quoteSent', 'Demande envoyée !')}
          </div>
        ) : (
          <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('fluxNode.promo.emailPlaceholder', 'Votre email')}
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: submitStatus === 'error' ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'var(--text)',
                fontSize: '13px',
                outline: 'none',
                transition: 'all 0.2s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#8b5cf6';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              disabled={isSubmitting}
            />
            <button
              onClick={handleSubmit}
              disabled={!email || isSubmitting}
              style={{
                padding: '12px 16px',
                background: (!email || isSubmitting)
                  ? 'rgba(139, 92, 246, 0.3)'
                  : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 600,
                cursor: (!email || isSubmitting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                opacity: (!email || isSubmitting) ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (email && !isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(139, 92, 246, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title={t('fluxNode.promo.getQuote', 'Obtenir un devis')}
            >
              {isSubmitting ? (
                <Icons.Activity size={18} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Icons.Send size={18} />
              )}
            </button>
          </div>
        )}

        {/* Wallet info hint */}
        {wallets.length > 0 && submitStatus !== 'success' && (
          <p style={{
            marginTop: '8px',
            fontSize: '10px',
            color: 'var(--muted)',
            textAlign: 'center',
          }}>
            {t('fluxNode.promo.walletsIncluded', '{{count}} wallet(s) seront inclus dans la demande', { count: wallets.length })}
          </p>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
